import { useState, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, X, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploader — drop into any admin form to replace URL text inputs.
 *
 * Props:
 *   value        — current URL (empty string if none)
 *   onChange     — (newUrl) => void
 *   accept       — "image" | "video" | "any"      (default: "image")
 *   purpose      — passed to backend for audit    (optional)
 *   label        — form label                     (default: "Image")
 *   testId       — data-testid prefix             (default: "uploader")
 */
const ImageUploader = ({
    value = "",
    onChange,
    accept = "image",
    purpose,
    label,
    testId = "uploader",
}) => {
    const { token } = useAuth();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [manualUrl, setManualUrl] = useState(value || "");

    const acceptAttr = accept === "video"
        ? "video/mp4,video/webm,video/quicktime"
        : accept === "any"
            ? "image/*,video/*"
            : "image/jpeg,image/png,image/webp,image/gif";

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (purpose) fd.append("purpose", purpose);
            const r = await axios.post(`${API}/admin/media/upload`, fd, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            // Backend returns a relative /uploads/... URL. Make it absolute.
            const url = r.data.url.startsWith("http") ? r.data.url : `${window.location.origin}${r.data.url}`;
            onChange?.(url);
            setManualUrl(url);
            toast.success("Uploaded", { description: `${(r.data.size / 1024).toFixed(1)} KB` });
        } catch (e) {
            toast.error("Upload failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setUploading(false);
        }
    };

    const clear = () => { onChange?.(""); setManualUrl(""); };

    const Icon = accept === "video" ? Video : ImageIcon;

    return (
        <div className="space-y-2" data-testid={testId}>
            {label && <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">{label}</label>}
            {value ? (
                <div className="relative rounded border border-border overflow-hidden bg-obsidian">
                    {accept === "video" ? (
                        <video src={value} className="w-full h-40 object-cover" controls />
                    ) : (
                        <img src={value} alt="" className="w-full h-40 object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                    )}
                    <button
                        onClick={clear}
                        className="absolute top-1 right-1 p-1 rounded bg-obsidian/80 text-white hover:bg-orange-500/80"
                        data-testid={`${testId}-clear`}
                        type="button"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-6 bg-obsidian hover:border-electric/60 transition-colors"
                    data-testid={`${testId}-dropzone`}
                >
                    {uploading ? (
                        <><Loader2 className="h-6 w-6 animate-spin text-electric" /><span className="text-xs text-white/60">Inapakia…</span></>
                    ) : (
                        <><Upload className="h-6 w-6 text-white/40" /><span className="text-xs text-white/60">Bofya kupakia {accept === "video" ? "video" : "picha"}</span></>
                    )}
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={acceptAttr}
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid={`${testId}-input`}
            />
            <div className="flex items-center gap-2">
                <Icon className="h-3 w-3 text-white/40 shrink-0" />
                <Input
                    value={manualUrl}
                    onChange={(e) => { setManualUrl(e.target.value); onChange?.(e.target.value); }}
                    placeholder="…au bandika URL ya nje"
                    className="bg-obsidian border-border text-white text-xs h-8"
                    data-testid={`${testId}-manual-url`}
                />
            </div>
        </div>
    );
};

export default ImageUploader;
